import React from 'react';
import { motion } from 'framer-motion';
import { Star, Shield, Users, Sparkles, Target, Zap } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
interface AboutPageProps {
  onNavigate: (page: string) => void;
}
export function AboutPage({
  onNavigate
}: AboutPageProps) {
  return <div className="min-h-screen bg-white pb-24" data-id="element-16048">
      {/* Hero Section */}
      <div className="bg-slate-50 border-b border-slate-200 pt-6 pb-20" data-id="element-16049">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-16050">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'О нас'
        }]} className="mb-12" data-id="element-16051" />

          <div className="max-w-3xl mx-auto text-center" data-id="element-16052">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-16053">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-16054">
                Делаем профессиональную астрологию{' '}
                <span className="text-indigo-600" data-id="element-16055">доступной</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-10" data-id="element-16056">
                AstroGuide — это платформа нового поколения, объединяющая точные
                алгоритмы расчета, глубокие знания профессиональных астрологов и
                современные технологии.
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-16057">
                <Button size="lg" onClick={() => onNavigate('service-natal')} data-id="element-16058">
                  Рассчитать карту
                </Button>
                <Button variant="outline" size="lg" onClick={() => onNavigate('astrologers')} data-id="element-16059">
                  Наши астрологи
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-12" data-id="element-16060">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 sm:p-12 relative z-10" data-id="element-16061">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center" data-id="element-16062">
            <div className="sm:border-r sm:border-slate-100" data-id="element-16063">
              <div className="text-4xl sm:text-5xl font-bold text-indigo-600 mb-2" data-id="element-16064">
                100k+
              </div>
              <div className="text-slate-500 font-medium" data-id="element-16065">
                Сгенерированных отчетов
              </div>
            </div>
            <div className="sm:border-r sm:border-slate-100" data-id="element-16066">
              <div className="text-4xl sm:text-5xl font-bold text-indigo-600 mb-2" data-id="element-16067">
                50+
              </div>
              <div className="text-slate-500 font-medium" data-id="element-16068">
                Проверенных астрологов
              </div>
            </div>
            <div data-id="element-16069">
              <div className="text-4xl sm:text-5xl font-bold text-indigo-600 mb-2" data-id="element-16070">
                98%
              </div>
              <div className="text-slate-500 font-medium" data-id="element-16071">
                Довольных клиентов
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission & Values */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-16072">
        <div className="text-center mb-16" data-id="element-16073">
          <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-16074">
            Наши принципы
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-16075">
            Мы верим, что астрология — это мощный инструмент самопознания,
            который должен быть точным, понятным и полезным.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-16076">
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-md transition-shadow" data-id="element-16077">
            <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mb-6" data-id="element-16078">
              <Target className="w-6 h-6" data-id="element-16079" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-16080">
              Высокая точность
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-16081">
              Наши алгоритмы используют швейцарские эфемериды NASA (Swiss
              Ephemeris), обеспечивая астрономическую точность расчетов,
              сопоставимую с профессиональным ПО.
            </p>
          </div>

          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-md transition-shadow" data-id="element-16082">
            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-6" data-id="element-16083">
              <Shield className="w-6 h-6" data-id="element-16084" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-16085">
              Строгий отбор
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-16086">
              Каждый астролог на платформе проходит многоэтапную проверку: мы
              проверяем дипломы, проводим собеседования и анализируем отзывы
              клиентов.
            </p>
          </div>

          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-md transition-shadow" data-id="element-16087">
            <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center mb-6" data-id="element-16088">
              <Sparkles className="w-6 h-6" data-id="element-16089" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-16090">
              Понятный язык
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-16091">
              Мы переводим сложный язык звезд на понятный человеческий. Наши
              отчеты написаны профессиональными астрологами, но читаются легко и
              увлекательно.
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-16092">
        <div className="bg-slate-900 rounded-3xl p-8 sm:p-12 text-center text-white relative overflow-hidden" data-id="element-16093">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-16094" />
          <div className="relative z-10" data-id="element-16095">
            <h2 className="text-3xl font-bold mb-4" data-id="element-16096">
              Готовы познакомиться с собой?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto" data-id="element-16097">
              Постройте свою натальную карту бесплатно и узнайте, как планеты
              влияют на вашу жизнь, характер и судьбу.
            </p>
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100" onClick={() => onNavigate('free-chart')} data-id="element-16098">
              Начать бесплатно
            </Button>
          </div>
        </div>
      </div>
    </div>;
}